import { RagDot, ConfidencePill, Card, HonestState, EmptyState, type Rag } from "../components.js";
import { DerivedMetric } from "./ProjectAnalyticsPanels.js";
import { blendDora, type DoraBearing } from "./dora-blend.js";
import "./program.css";

/**
 * E8.4 (NX-2631) — the program-rollup body on the existing project detail route (`Project.tsx`
 * renders `RollupTree` + `BlendedDoraPanel` when `GET /projects/:id` returns `subProjects`; that
 * field has been populated since NX-2617/E5.4's dependency-aware program work, and each sub-project
 * has carried its own `dora: DoraDerivation` since NX-2616/E5.2 — both landed on main before this
 * story started, which is why the blended-DORA panel below renders real data rather than the
 * "not observable, E5.2/E5.4 hasn't landed" state the original ticket text described).
 *
 * SOLID split (AC4): `RollupTree` lays out the tree, `SubProjectCard` renders one sub-project,
 * `BlendedDoraPanel` renders the program-level blend. `Project.tsx` only decides WHEN to mount this
 * section (its own subProjects !== undefined check) and passes the already-fetched data through —
 * neither this module nor its components issue a second request (AC4's "reads data ONLY via the
 * existing useApi hook" is enforced by construction: nothing here imports useApi or fetch at all).
 */

const STATUS_RAG: Record<string, Rag> = {
  green: "green",
  yellow: "amber",
  red: "red",
  unsupported: "neutral",
};
const STATUS_LABEL: Record<string, string> = {
  green: "On track",
  yellow: "At risk",
  red: "Off track",
  unsupported: "Unsupported",
};
const ragOf = (status: string): Rag => STATUS_RAG[status] ?? "neutral";
const labelOf = (status: string): string => STATUS_LABEL[status] ?? status;
const DAY_SECONDS = 86_400;
const decimal = (value: number, places = 1): string => String(Number(value.toFixed(places)));

/** What RollupTree/SubProjectCard need from a sub-project — structurally satisfied by the full
 *  ProjectView the API returns, so Project.tsx passes `data.subProjects` straight through. */
export interface SubProjectView extends DoraBearing {
  readonly id: string;
  readonly name: string;
  readonly scopeType: string;
  readonly status: string;
  readonly confidence: number | null;
  readonly reason: string;
}

/**
 * One sub-project (AC1/AC3). `confidence === null` is the honest-N/A case (`computeStatus` in
 * packages/core/src/status.ts only ever returns a null confidence alongside `status: "unsupported"`,
 * when NO dimension was observable) — that renders through `HonestState`, never a blank tile or a
 * `ConfidencePill` reading as a zeroed number.
 */
function SubProjectCard({ sub }: { sub: SubProjectView }): React.ReactNode {
  const honestNA = sub.confidence === null;
  return (
    <Card href={`/projects/${sub.id}`}>
      <div className="subhead">
        <RagDot status={ragOf(sub.status)} label={labelOf(sub.status)} />
        <span className="subname">{sub.name}</span>
      </div>
      <div className="substype">{sub.scopeType}</div>
      {honestNA ? (
        <HonestState kind="notobs" detail={sub.reason} />
      ) : (
        <>
          <div className="subreason">{sub.reason}</div>
          <ConfidencePill value={sub.confidence} />
        </>
      )}
    </Card>
  );
}

/**
 * The rollup tree itself (AC1/AC2). An empty `subProjects` array is a real, distinct state from "not
 * a program at all" — `Project.tsx` only mounts this component when the API returned the
 * `subProjects` key, so by the time this renders, the caller already knows it IS a program; a program
 * with zero children must still say so honestly rather than the section silently vanishing (TC2 — the
 * bug this story fixes: the previous inline rendering skipped the whole "Sub-projects" section
 * whenever the array was empty, which reads identically to "not a program" from the screen).
 */
export function RollupTree({
  subProjects,
}: {
  subProjects: readonly SubProjectView[];
}): React.ReactNode {
  if (subProjects.length === 0) {
    return <EmptyState message="This program has no sub-projects yet." />;
  }
  return (
    <div className="grid cols-3">
      {subProjects.map((sub) => (
        <SubProjectCard key={sub.id} sub={sub} />
      ))}
    </div>
  );
}

/**
 * The blended-DORA panel (AC2). Delegates every number to `blendDora` (dora-blend.ts) — this
 * component only renders whatever that pure function returns, through the same `DerivedMetric`
 * tile-or-honest-state split the sibling reliability/friction/capability panels use, so a metric
 * neither sub-project observed reads as the honest state rather than a naked zero.
 */
export function BlendedDoraPanel({
  subProjects,
}: {
  subProjects: readonly SubProjectView[];
}): React.ReactNode {
  const blended = blendDora(subProjects);
  return (
    <>
      <div className="sectitle">
        <h3 className="sec">Blended DORA (program rollup)</h3>
        <span className="cnt">
          deploy frequency · lead time for change · change-failure rate · time to restore —
          aggregated across the sub-projects with observed data
        </span>
      </div>
      <div className="grid cols-2">
        <DerivedMetric
          label="Deploy frequency"
          metric={blended.deployFrequency}
          signal={(v) => v.perDay}
          render={(v) => (
            <>
              {decimal(v.perDay, 2)}{" "}
              <small>
                per day · {v.subProjectsObserved}/{v.subProjectsTotal} sub-projects
              </small>
            </>
          )}
        />
        <DerivedMetric
          label="Lead time for change"
          metric={blended.leadTimeForChange}
          render={(v) => (
            <>
              {decimal(v.meanSeconds / DAY_SECONDS)}{" "}
              <small>
                days mean · {v.subProjectsObserved}/{v.subProjectsTotal} sub-projects
              </small>
            </>
          )}
        />
        <DerivedMetric
          label="Change-failure rate"
          metric={blended.changeFailureRate}
          render={(v) => (
            <>
              {decimal(v.failureRate * 100)}%{" "}
              <small>
                of {v.succeeded + v.failed} decided · {v.subProjectsObserved}/{v.subProjectsTotal}{" "}
                sub-projects
              </small>
            </>
          )}
        />
        <DerivedMetric
          label="Time to restore"
          metric={blended.timeToRestore}
          render={(v) => (
            <>
              {decimal(v.meanSeconds / DAY_SECONDS)}{" "}
              <small>
                days mean · {v.subProjectsObserved}/{v.subProjectsTotal} sub-projects
              </small>
            </>
          )}
        />
      </div>
    </>
  );
}
